/**
 * Build PUT /internal-agenda bodies from the agenda builder (minimal valid rows; matches server Zod schema).
 */

/** @typedef {{ internalAgendaItemId: string, linkedPublicAgendaItemId: string, desiredOutcome: string, privateNotes: string, visibilityScope?: 'shared_internal_team' | 'member_private', authorUserId?: string | null }} InternalGoalRow */

export const INTERNAL_DESIRED_OUTCOMES = [
  { value: 'close_deal', label: 'Close deal' },
  { value: 'gather_information', label: 'Gather information' },
  { value: 'align_scope', label: 'Align scope' },
  { value: 'negotiate_terms', label: 'Negotiate terms' },
  { value: 'maintain_relationship', label: 'Maintain relationship' },
  { value: 'other', label: 'Other' },
]

export function newInternalGoalId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `ia-${crypto.randomUUID()}`
  }
  return `ia-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

/** @param {object} p */
export function minimalInternalAgendaItem(p) {
  const {
    internalAgendaItemId,
    order,
    linkedPublicAgendaItemId,
    desiredOutcome,
    privateNotes,
    visibilityScope,
    authorUserId,
  } = p
  const notes =
    typeof privateNotes === 'string' && privateNotes.trim()
      ? privateNotes.trim().slice(0, 1000)
      : null
  return {
    internalAgendaItemId,
    order,
    visibilityScope: visibilityScope === 'member_private' ? 'member_private' : 'shared_internal_team',
    authorUserId:
      visibilityScope === 'member_private' && typeof authorUserId === 'string' && authorUserId.trim()
        ? authorUserId.trim()
        : null,
    linkedPublicAgendaItemId:
      linkedPublicAgendaItemId && String(linkedPublicAgendaItemId).trim()
        ? String(linkedPublicAgendaItemId).trim()
        : null,
    desiredOutcome,
    targetType: 'none',
    targetValue: { kind: 'none' },
    concessionBounds: { kind: 'none' },
    redLineType: 'none',
    redLineParams: null,
    neverSayTags: [],
    privateNotes: notes,
  }
}

/**
 * @param {InternalGoalRow[]} goals
 * @param {{ defaultVisibilityScope?: 'shared_internal_team' | 'member_private', authorUserId?: string | null }} [options]
 * @returns {{ schemaVersion: 1, items: unknown[], activeInternalAgendaItemId: string | null }}
 */
export function buildInternalAgendaStateFromGoals(goals, options = {}) {
  const safe = Array.isArray(goals) ? goals : []
  const defaultVisibilityScope =
    options.defaultVisibilityScope === 'member_private' ? 'member_private' : 'shared_internal_team'
  const items = safe.map((g, i) =>
    minimalInternalAgendaItem({
      internalAgendaItemId: g.internalAgendaItemId || newInternalGoalId(),
      order: i,
      visibilityScope: g.visibilityScope ?? defaultVisibilityScope,
      authorUserId: g.authorUserId ?? options.authorUserId ?? null,
      linkedPublicAgendaItemId: g.linkedPublicAgendaItemId,
      desiredOutcome: g.desiredOutcome || 'gather_information',
      privateNotes: g.privateNotes,
    })
  )
  return {
    schemaVersion: 1,
    items,
    activeInternalAgendaItemId: items.length > 0 ? items[0].internalAgendaItemId : null,
  }
}

export function buildMemberPrivateAgendaStateFromGoals(goals, authorUserId) {
  return buildInternalAgendaStateFromGoals(goals, {
    defaultVisibilityScope: 'member_private',
    authorUserId,
  })
}

/** Clears stored internal goals when the meeting is team-only (internal audience). */
export function emptyInternalAgendaState() {
  return {
    schemaVersion: 1,
    items: [],
    activeInternalAgendaItemId: null,
  }
}

/**
 * @param {unknown} state
 * @returns {InternalGoalRow[]}
 */
export function mapApiInternalAgendaToGoals(state) {
  if (!state || typeof state !== 'object' || !Array.isArray(state.items)) return []
  return state.items
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((row) => ({
      internalAgendaItemId: row.internalAgendaItemId,
      linkedPublicAgendaItemId: row.linkedPublicAgendaItemId ?? '',
      desiredOutcome: row.desiredOutcome ?? 'gather_information',
      privateNotes: row.privateNotes ?? '',
      visibilityScope: row.visibilityScope === 'member_private' ? 'member_private' : 'shared_internal_team',
      authorUserId: typeof row.authorUserId === 'string' ? row.authorUserId : null,
    }))
}
